/**
 * DecisionFlow — Set Password (Invite)
 * ui/src/screens/SetPassword.jsx
 * data-fit LLC · S664 · July 31, 2026
 *
 * Public route: /set-password?token=<raw_token>
 * No auth required. Consumes invite token and sets first password.
 * Calls POST /api/v1/auth/set-password.
 * On success → navigate to /login with success message in location.state.
 *
 * spec_auth_identity_s661.md §3.2 — invite token consumption
 */

import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "/api/v1";

function checkComplexity(pw) {
  if (pw.length < 10)          return "Password must be at least 10 characters.";
  if (!/[A-Z]/.test(pw))      return "Password must contain at least one uppercase letter.";
  if (!/[a-z]/.test(pw))      return "Password must contain at least one lowercase letter.";
  if (!/[0-9]/.test(pw))      return "Password must contain at least one number.";
  return null;
}

function errorFromCode(code, message) {
  switch (code) {
    case "TOKEN_INVALID":
      return "This invite link is invalid or has already been used. Contact your administrator to resend the invite.";
    case "PASSWORD_COMPLEXITY_VIOLATION":
      return "Password does not meet complexity requirements.";
    default:
      return message || "Something went wrong. Please try again.";
  }
}

export default function SetPassword() {
  const [password,        setPassword]        = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [submitting,      setSubmitting]      = useState(false);
  const [error,           setError]           = useState(null);
  const [showPassword,    setShowPassword]    = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Token delivered via invite email link: /set-password?token=<raw>
  const token = new URLSearchParams(location.search).get("token") ?? "";

  const missingToken = !token;

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (password !== passwordConfirm) {
      setError("Passwords do not match.");
      return;
    }

    const complexityError = checkComplexity(password);
    if (complexityError) {
      setError(complexityError);
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/auth/set-password`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          token,
          password,
          password_confirm: passwordConfirm,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const code = data?.error?.code ?? "";
        const msg  = data?.error?.message ?? "";
        setError(errorFromCode(code, msg));
        return;
      }

      // Success — navigate to login with success banner
      navigate("/login", {
        replace: true,
        state: { successMessage: "Password set. Welcome to DecisionFlow — please sign in." },
      });
    } catch (_) {
      setError("Unable to connect. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Scoped CSS ──────────────────────────────────────────────────────────────
  const css = `
    .dfsp-root {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: rgb(var(--color-bg));
      font-family: "IBM Plex Sans", "Segoe UI", Arial, sans-serif;
      padding: 24px;
    }

    .dfsp-card {
      width: 100%;
      max-width: 420px;
      background: rgb(var(--color-surface));
      border: 1px solid rgb(var(--color-border));
      border-radius: 14px;
      padding: 40px 40px 36px;
      box-shadow: 0 4px 24px rgba(0,0,0,0.06);
    }

    .dfsp-identity { margin-bottom: 28px; }
    .dfsp-identity-brand {
      font-size: var(--font-sz-xs);
      font-weight: 500;
      color: rgb(var(--color-text-muted));
      letter-spacing: 0.04em;
      margin-bottom: 3px;
    }
    .dfsp-identity-name {
      font-size: var(--font-sz-xl);
      font-weight: 700;
      color: rgb(var(--color-primary));
      letter-spacing: -0.01em;
      line-height: 1.1;
    }

    .dfsp-header { margin-bottom: 24px; }
    .dfsp-title {
      font-size: var(--font-sz-lg);
      font-weight: 700;
      color: rgb(var(--color-text));
      margin-bottom: 6px;
    }
    .dfsp-subtitle {
      font-size: var(--font-sz-sm);
      color: rgb(var(--color-text-muted));
      line-height: 1.5;
    }

    .dfsp-welcome-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: rgba(var(--color-primary-rgb, var(--color-primary)), 0.08);
      border: 1px solid rgba(var(--color-primary-rgb, var(--color-primary)), 0.2);
      color: rgb(var(--color-primary));
      font-size: var(--font-sz-xs);
      font-weight: 600;
      padding: 4px 12px;
      border-radius: 20px;
      margin-bottom: 20px;
    }

    .dfsp-error-banner {
      background: rgba(var(--color-danger-rgb, 220,38,38), 0.08);
      border: 1px solid rgba(var(--color-danger-rgb, 220,38,38), 0.2);
      border-radius: 8px;
      padding: 10px 14px;
      font-size: var(--font-sz-sm);
      color: rgb(var(--color-danger));
      margin-bottom: 20px;
      line-height: 1.5;
    }

    .dfsp-field { margin-bottom: 18px; }
    .dfsp-label {
      display: block;
      font-size: var(--font-sz-sm);
      font-weight: 500;
      color: rgb(var(--color-text));
      margin-bottom: 6px;
    }
    .dfsp-input-wrap {
      position: relative;
      display: flex;
      align-items: center;
    }
    .dfsp-input {
      width: 100%;
      padding: 10px 40px 10px 13px;
      border: 1px solid rgb(var(--color-border));
      border-radius: 7px;
      background: rgb(var(--color-surface));
      color: rgb(var(--color-text));
      font-size: var(--font-sz-sm);
      font-family: inherit;
      transition: border-color 150ms, box-shadow 150ms;
      outline: none;
      box-sizing: border-box;
    }
    .dfsp-input::placeholder {
      color: rgb(var(--color-placeholder));
      font-style: italic;
    }
    .dfsp-input:focus {
      border-color: rgb(var(--color-focus-ring));
      box-shadow: 0 0 0 3px rgba(var(--color-focus-ring), 0.15);
    }
    .dfsp-input:disabled { opacity: 0.5; cursor: not-allowed; }

    .dfsp-reveal {
      position: absolute;
      right: 10px;
      background: none;
      border: none;
      cursor: pointer;
      color: rgb(var(--color-text-muted));
      font-size: var(--font-sz-xs);
      padding: 4px;
      font-family: inherit;
      line-height: 1;
    }
    .dfsp-reveal:hover { color: rgb(var(--color-text)); }

    .dfsp-hint {
      font-size: var(--font-sz-xs);
      color: rgb(var(--color-text-muted));
      margin-top: 5px;
      line-height: 1.4;
    }

    .dfsp-submit {
      width: 100%;
      padding: 12px;
      background: rgb(var(--color-primary));
      color: #ffffff;
      border: none;
      border-radius: 7px;
      font-size: var(--font-sz-base);
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      transition: background 150ms;
      letter-spacing: 0.01em;
      margin-bottom: 16px;
    }
    .dfsp-submit:hover:not(:disabled) { background: rgb(var(--color-primary-hover)); }
    .dfsp-submit:disabled { opacity: 0.5; cursor: not-allowed; }

    /* Invalid link state */
    .dfsp-invalid-icon {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: rgba(var(--color-danger-rgb, 220,38,38), 0.08);
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 20px;
      font-size: 22px;
    }
    .dfsp-invalid-title {
      font-size: var(--font-sz-lg);
      font-weight: 700;
      color: rgb(var(--color-text));
      text-align: center;
      margin-bottom: 10px;
    }
    .dfsp-invalid-body {
      font-size: var(--font-sz-sm);
      color: rgb(var(--color-text-muted));
      text-align: center;
      line-height: 1.6;
      margin-bottom: 28px;
    }
  `;

  return (
    <>
      <style>{css}</style>
      <div className="dfsp-root">
        <div className="dfsp-card">

          {/* Product identity */}
          <div className="dfsp-identity">
            <div className="dfsp-identity-brand">data-fit LLC</div>
            <div className="dfsp-identity-name">DecisionFlow</div>
          </div>

          {missingToken ? (
            /* ── Invalid / missing token ── */
            <>
              <div className="dfsp-invalid-icon" aria-hidden="true">⚠</div>
              <div className="dfsp-invalid-title">Invalid invite link</div>
              <div className="dfsp-invalid-body">
                This link is missing an invite token. Use the link from your
                invitation email. If the link has expired, contact your
                administrator to resend the invitation.
              </div>
              <button
                className="dfsp-submit"
                onClick={() => navigate("/login")}
              >
                Go to sign in
              </button>
            </>
          ) : (
            /* ── Set password form ── */
            <>
              <div className="dfsp-welcome-badge">
                ✦ You've been invited
              </div>

              <div className="dfsp-header">
                <div className="dfsp-title">Set your password</div>
                <div className="dfsp-subtitle">
                  Choose a strong password to activate your DecisionFlow account.
                </div>
              </div>

              {error && (
                <div className="dfsp-error-banner" role="alert">{error}</div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <div className="dfsp-field">
                  <label className="dfsp-label" htmlFor="dfsp-password">
                    Password
                  </label>
                  <div className="dfsp-input-wrap">
                    <input
                      id="dfsp-password"
                      className="dfsp-input"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      autoFocus
                      required
                      disabled={submitting}
                      placeholder="••••••••••"
                    />
                    <button
                      type="button"
                      className="dfsp-reveal"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                  <div className="dfsp-hint">
                    10+ characters · uppercase · lowercase · number
                  </div>
                </div>

                <div className="dfsp-field">
                  <label className="dfsp-label" htmlFor="dfsp-confirm">
                    Confirm password
                  </label>
                  <div className="dfsp-input-wrap">
                    <input
                      id="dfsp-confirm"
                      className="dfsp-input"
                      type={showPassword ? "text" : "password"}
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      autoComplete="new-password"
                      required
                      disabled={submitting}
                      placeholder="••••••••••"
                    />
                  </div>
                </div>

                <button
                  className="dfsp-submit"
                  type="submit"
                  disabled={submitting || !password || !passwordConfirm}
                >
                  {submitting ? "Activating account…" : "Activate account"}
                </button>
              </form>
            </>
          )}

        </div>
      </div>
    </>
  );
}
